import React, { useEffect, useState } from "react";
import {
  FaImage,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
} from "react-icons/fa";
import API, { getData, postData, putData, uploadUrl } from "../utils/api";

const emptyForm = {
  title: "",
  image: "",
  imageFile: null,
};

const AdminBanner = () => {
  const [banners, setBanners] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);

  const getImageSrc = (image) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    return `${uploadUrl}/${image}`;
  };

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const data = await getData("/banners");
      setBanners(data.data || data || []);
    } catch (error) {
      console.log(error);
      alert("Banner fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleInput = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      setFormData((prev) => ({
        ...prev,
        imageFile: files[0],
        image: URL.createObjectURL(files[0]),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      title: item.title || "",
      image: getImageSrc(item.image),
      imageFile: null,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!editingId && !formData.imageFile) {
      alert("Please upload banner image");
      return;
    }

    try {
      const body = new FormData();
      body.append("title", formData.title || "Website Banner");

      if (formData.imageFile) {
        body.append("image", formData.imageFile);
      }

      if (editingId) {
        await putData(`/banners/${editingId}`, body);
        alert("Banner updated successfully");
      } else {
        await postData("/banners", body);
        alert("Banner added successfully");
      }

      setShowForm(false);
      setEditingId(null);
      setFormData(emptyForm);
      fetchBanners();
    } catch (error) {
      console.log(error.response?.data || error);
      alert(error.response?.data?.message || "Banner save failed");
    }
  };

  const confirmDelete = async () => {
    try {
      await API.delete(`/banners/${deleteId}`);
      setBanners((prev) => prev.filter((item) => item._id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.log(error);
      alert(error.response?.data?.message || "Banner delete failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-[#D8E6FF] shadow-sm p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#073B7A]">
            Banner Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Add, edit and delete website banners.
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="h-12 px-5 rounded-xl bg-[#073B7A] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#0B4EA2]"
        >
          <FaPlus />
          Add Banner
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-[#073B7A] font-semibold">
          Loading banners...
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {banners.map((item) => (
            <div
              key={item._id}
              className="bg-white rounded-2xl border border-[#D8E6FF] shadow-sm overflow-hidden"
            >
              <img
                src={getImageSrc(item.image)}
                alt={item.title || "Banner"}
                className="w-full h-[240px] object-cover"
              />

              <div className="p-5 flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold text-[#073B7A]">
                    {item.title || "Website Banner"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Banner Image</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="w-10 h-10 rounded-lg bg-[#0B4EA2] text-white flex items-center justify-center"
                  >
                    <FaEdit />
                  </button>

                  <button
                    onClick={() => setDeleteId(item._id)}
                    className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {banners.length === 0 && (
            <div className="md:col-span-2 bg-white border border-dashed border-[#D8E6FF] rounded-2xl p-10 text-center">
              <FaImage className="mx-auto text-5xl text-[#073B7A]" />
              <h3 className="mt-4 text-lg font-semibold text-[#073B7A]">
                No Banner Found
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Upload your first banner.
              </p>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-xl overflow-hidden">
            <div className="bg-[#073B7A] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="font-semibold text-lg">
                {editingId ? "Update Banner" : "Add Banner"}
              </h3>

              <button onClick={() => setShowForm(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInput}
                placeholder="Banner Title"
                className="w-full px-4 py-3 rounded-xl border border-[#D8E6FF] outline-none"
              />

              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleInput}
                className="w-full px-4 py-3 rounded-xl border border-[#D8E6FF] outline-none"
              />

              {formData.image && (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-full h-[260px] object-cover rounded-2xl border"
                />
              )}

              <button
                onClick={handleSave}
                className="w-full h-12 rounded-xl bg-[#073B7A] text-white font-semibold hover:bg-[#0B4EA2] flex items-center justify-center gap-2"
              >
                <FaSave />
                {editingId ? "Update Banner" : "Add Banner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-xl p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 mx-auto flex items-center justify-center text-2xl">
              <FaTrash />
            </div>

            <h3 className="mt-4 text-xl font-semibold text-[#073B7A]">
              Delete Banner?
            </h3>

            <p className="mt-2 text-sm text-gray-500">
              This banner will be permanently deleted.
            </p>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 h-11 rounded-xl border border-[#0B4EA2] text-[#0B4EA2]"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="flex-1 h-11 rounded-xl bg-red-600 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanner;